import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2, ChevronDown } from 'lucide-react';

/** Generic type-to-search combo with paging ("Load more"), keyboard nav and a clear button.
 *  fetchPage(q, page) → { items, total }.  renderRow(item) → node.  getKey(item) → string. */
export function SearchCombo({
  value, displayLabel, onSelect, fetchPage, renderRow, getKey, placeholder = 'Type to search…',
  emptyText = (q) => (q ? `No match for '${q}'` : 'Nothing to show'), testid = 'search-combo',
  disabled = false, note = null, debounceMs = 250, compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const timer = useRef(null);

  const load = useCallback(async (term, pg, append) => {
    setLoading(true);
    try {
      const d = await fetchPage(term, pg);
      setRows(prev => (append ? [...prev, ...(d.items || [])] : (d.items || [])));
      setTotal(Number(d.total ?? (d.items || []).length));
      setPage(pg);
    } catch { if (!append) setRows([]); } finally { setLoading(false); }
  }, [fetchPage]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => load(q, 1, false), debounceMs);
    return () => clearTimeout(timer.current);
  }, [q, open, load, debounceMs]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const pick = (item) => { onSelect(item); setOpen(false); setQ(''); };
  const onKey = (e) => {
    if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (rows[active]) pick(rows[active]); }
    else if (e.key === 'Escape') setOpen(false);
  };
  const hasMore = rows.length < total;
  const pad = compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm';

  return (
    <div ref={boxRef} className="relative" data-testid={testid}>
      {value && !open ? (
        <div className={`flex items-center gap-2 border rounded-lg bg-white ${pad}`} data-testid={`${testid}-selected`}>
          <button type="button" className="flex-1 text-left truncate" onClick={() => !disabled && setOpen(true)} data-testid={`${testid}-change`}>{displayLabel}</button>
          {!disabled && <button type="button" onClick={() => onSelect(null)} className="text-slate-400 hover:text-rose-600" aria-label="Clear" data-testid={`${testid}-clear`}><X className="w-4 h-4" /></button>}
        </div>
      ) : (
        <div className={`flex items-center gap-2 border rounded-lg bg-white ${pad} ${disabled ? 'opacity-60' : ''}`}>
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <input value={q} disabled={disabled} onChange={e => { setQ(e.target.value); setActive(0); setOpen(true); }}
            onFocus={() => setOpen(true)} onKeyDown={onKey} placeholder={placeholder}
            className="flex-1 outline-none bg-transparent min-w-0" data-testid={`${testid}-input`} autoComplete="off" />
          {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      )}
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-72 overflow-y-auto" data-testid={`${testid}-list`} role="listbox">
          {note && <div className="px-3 py-1.5 text-[11px] text-amber-700 bg-amber-50 border-b">{note}</div>}
          {rows.length === 0 && !loading && <div className="px-3 py-3 text-xs text-slate-500" data-testid={`${testid}-empty`}>{emptyText(q)}</div>}
          {rows.map((it, i) => (
            <button type="button" key={getKey(it)} role="option" aria-selected={i === active}
              onMouseEnter={() => setActive(i)} onClick={() => pick(it)}
              className={`w-full text-left px-3 py-2 text-xs border-b last:border-0 ${i === active ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
              data-testid={`${testid}-option-${getKey(it)}`}>
              {renderRow(it)}
            </button>
          ))}
          {hasMore && (
            <button type="button" onClick={() => load(q, page + 1, true)} disabled={loading}
              className="w-full px-3 py-2 text-xs font-medium text-[#3b4872] hover:bg-slate-50" data-testid={`${testid}-load-more`}>
              {loading ? 'Loading…' : `Load more (${rows.length} of ${total})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
